import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { Page } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PlugsConnected, Buildings, CheckCircle, Circle, LinkSimple, Plugs, Trash, Warning, Sparkle, MagnifyingGlass } from "@phosphor-icons/react";
import DisconnectClientModal from "@/components/DisconnectClientModal";
import CreateAdjustmentModal from "@/components/CreateAdjustmentModal";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function ConnectionsPanel() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [diag, setDiag] = useState(null);
  const [showError, setShowError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [health, setHealth] = useState({});
  const [testing, setTesting] = useState(false);
  const [disconnectOrg, setDisconnectOrg] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [showClear, setShowClear] = useState(false);
  const isAdmin = user?.role === "admin";
  const isSuperadmin = user?.role === "superadmin";

  const load = useCallback(async () => {
    const { data } = await api.get("/xero/status");
    setStatus(data);
    setSelected(new Set(data.organisations.filter((o) => o.selected).map((o) => o.tenant_id)));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/xero/diagnostics").then(({ data }) => setDiag(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (params.get("connected") === "1") {
      toast.success("Xero connected");
      params.delete("connected");
      setParams(params, { replace: true });
    } else if (params.get("error") === "1") {
      toast.error("Xero connection failed or was cancelled. Please try again.");
      setShowError(true);
      params.delete("error");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const copyCallback = () => {
    if (!diag?.redirect_uri) return;
    navigator.clipboard?.writeText(diag.redirect_uri);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/xero/connect");
      const url = data.mocked && data.auth_url_relative
        ? `${window.location.origin}${data.auth_url_relative}`
        : data.auth_url;
      // The app runs inside an iframe; Xero refuses to be framed (X-Frame-Options).
      // Open the authorisation flow in a top-level new tab instead.
      // NOTE: do NOT pass "noopener" — that forces window.open to return null even on success.
      const win = window.open(url, "_blank");
      if (win && !win.closed) {
        win.focus();
        toast.info("Continue in the new Xero tab, then return here and refresh.");
      } else {
        // Popup blocked: break out of the iframe to the top window.
        try {
          window.top.location.href = url;
        } catch {
          window.location.href = url;
        }
      }
    } catch {
      toast.error("Could not start Xero connection");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await api.post("/xero/disconnect");
    toast.success("Disconnected");
    load();
  };

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/xero/organisations/select", { tenant_ids: [...selected] });
      toast.success("Organisation selection saved");
      load();
    } finally { setBusy(false); }
  };

  const testConnections = async () => {
    setTesting(true);
    try {
      const { data } = await api.get("/xero/test");
      const map = {};
      (data.results || []).forEach((r) => { map[r.tenant_id] = r; });
      setHealth(map);
      const bad = (data.results || []).filter((r) => !r.ok).length;
      if (!data.token_ok) toast.error("Xero session expired — reconnect required.");
      else if (bad) toast.warning(`${bad} organisation(s) failed the connection test.`);
      else toast.success("All organisations connected.");
    } catch {
      toast.error("Could not test the Xero connection.");
    } finally { setTesting(false); }
  };

  const resetData = async () => {
    if (!resetPassword) { toast.error("Enter your password to confirm"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/data/reset", { clear_audit: true, password: resetPassword });
      const d = data.deleted;
      try { sessionStorage.removeItem("closeNewDraft"); } catch { /* noop */ }
      toast.success(`Cleared ${data.org_name || "this organisation"} — ${d.items} items, ${d.schedules} schedules, ${d.journals} journals`);
      setShowClear(false); setResetPassword(""); load();
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Password is incorrect — data not cleared" : "Could not clear workspace data");
    } finally { setBusy(false); }
  };

  const seedDemo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/demo/seed");
      toast.success(`Demo ${demoLoaded ? "reset" : "loaded"} — ${data.entities} entities, ${data.schedules} schedules. Active: ${data.active_org}`);
      await load();
      window.dispatchEvent(new Event("badges:refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load demo data");
    } finally { setBusy(false); }
  };

  const demoLoaded = status?.organisations?.some((o) => o.demo);
  const activeOrgName = status?.organisations?.find((o) => o.tenant_id === status?.active_tenant_id)?.tenant_name
    || status?.organisations?.find((o) => o.active)?.tenant_name || "";

  const connected = status?.connection?.status === "connected";
  const mocked = status?.connection?.mocked;
  const liveReady = status?.xero_configured;

  return (
    <>
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="overline text-orangeink mb-1">Integration</div>
          <h2 className="font-heading font-bold text-2xl tracking-tight text-navy">Connect to Xero</h2>
          <p className="text-sm text-navy/70 mt-1 max-w-2xl">Connect Xero via OAuth and choose which organisations to manage. Xero is the only supported platform.</p>
        </div>
        {connected ? (
          <div className="flex items-center gap-2">
            <button onClick={connect} disabled={busy} data-testid="xero-add-org-btn"
              className="flex items-center gap-2 bg-orange text-white px-4 py-2 text-sm rounded-lg font-medium hover:bg-navy transition-colors duration-200 disabled:opacity-60">
              <LinkSimple size={16} /> {busy ? "OPENING…" : "Add organisation"}
            </button>
            <button onClick={disconnect} data-testid="xero-disconnect-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:border-navy hover:bg-navy/5 transition-all">
              <Plugs size={16} /> Disconnect
            </button>
          </div>
        ) : null}
      </div>

      {/* Regular customers: friendly message only if a connection genuinely failed. */}
      {!connected && showError && !isSuperadmin && (
        <div className="rounded-lg p-5 mb-6 border bg-negative/[0.05] border-negative/30" data-testid="xero-error-banner">
          <div className="flex items-start gap-3">
            <Warning size={22} className="text-negative shrink-0 mt-0.5" weight="fill" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-navy mb-1">Couldn't connect to Xero</h4>
              <p className="text-sm text-navy/65">
                Something went wrong while authorising with Xero. Please try connecting again — if it keeps
                happening, contact support and we'll take a look.
              </p>
              <button onClick={() => setShowError(false)} className="text-xs text-navy/70 hover:text-navy mt-3" data-testid="xero-error-dismiss">Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {/* Superadmin/owner only: technical diagnostic for the central Xero app config. */}
      {!connected && diag && isSuperadmin && (
        <div className={`rounded-lg p-5 mb-6 border ${showError ? "bg-negative/[0.05] border-negative/30" : "bg-orange/[0.04] border-orange/25"}`} data-testid="xero-error-banner">
          <div className="flex items-start gap-3">
            <Warning size={22} className={`${showError ? "text-negative" : "text-orangeink"} shrink-0 mt-0.5`} weight="fill" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-navy mb-1">
                {showError ? "Xero rejected the connection" : "Before you connect — verify your Xero app"}
              </h4>
              <p className="text-sm text-navy/65 mb-3">
                These are the <b>exact</b> values this app sends to Xero. Both must be saved in your Xero app at
                {" "}developer.xero.com → your app → <b>Configuration</b> → <b>Save</b>. The redirect URI must match
                {" "}character-for-character (https, no trailing slash).
              </p>
              <div className="space-y-2">
                <div>
                  <span className="overline text-navy/65 block mb-1">Redirect URI to register in Xero</span>
                  <div className="flex items-center gap-2">
                    <code data-testid="xero-expected-redirect" className="flex-1 text-xs font-mono bg-white border border-slate-200 rounded-lg px-3 py-2 text-navy break-all">{diag.redirect_uri}</code>
                    <button onClick={copyCallback} data-testid="xero-copy-redirect"
                      className="shrink-0 text-xs font-medium border border-slate-200 text-navy px-3 py-2 rounded-lg hover:bg-navy hover:text-white transition-colors">
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
                <div>
                  <span className="overline text-navy/65 block mb-1">Client ID (must exactly match your Xero app)</span>
                  <code data-testid="xero-client-id" className="text-xs font-mono bg-white border border-slate-200 rounded-lg px-3 py-2 text-navy/70 inline-block break-all">{diag.client_id}</code>
                </div>
              </div>
              {showError && (
                <button onClick={() => setShowError(false)} className="text-xs text-navy/70 hover:text-navy mt-3" data-testid="xero-error-dismiss">Dismiss</button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${connected ? "bg-positive/10 text-positive" : "bg-navy/5 text-navy/70"}`}>
            <PlugsConnected size={26} weight="fill" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg text-navy tracking-tight">Xero</div>
            <div className="text-sm text-navy/70">
              {connected ? <>Connected {fmtDate(status.connection.connected_at)} · {status.connection.tenant_count} organisation(s)</>
                : liveReady ? "Not connected — ready to authorise with Xero" : "Not connected"}
              {connected && mocked && (
                <span className="ml-2 text-xs font-mono px-1.5 py-0.5 bg-warn/10 text-warn rounded-md border border-warn/30">MOCKED</span>
              )}
              {connected && !mocked && (
                <span className="ml-2 text-xs font-mono px-1.5 py-0.5 bg-positive/10 text-positive rounded-md border border-positive/30">LIVE</span>
              )}
            </div>
          </div>
        </div>
        {!connected && (
          <button onClick={connect} disabled={busy} data-testid="xero-oauth-btn"
            className="flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy transition-colors duration-200 disabled:opacity-60">
            <LinkSimple size={18} /> {busy ? "CONNECTING…" : "Connect to Xero"}
          </button>
        )}
      </div>

      {connected && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div>
              <div className="overline text-navy/65">Organisations</div>
              <div className="text-sm text-navy/70 mt-0.5">Select one or more to import from. Use <b>Add organisation</b> above to authorise more entities under the same Xero login — existing ones are kept.</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={testConnections} disabled={testing} data-testid="test-connections-btn"
                className="flex items-center gap-2 border border-slate-200 text-navy px-4 py-2 rounded-lg text-sm font-medium hover:border-navy hover:bg-navy/5 transition-all disabled:opacity-60">
                <PlugsConnected size={16} /> {testing ? "Testing…" : "Test connections"}
              </button>
              <button onClick={save} disabled={busy} data-testid="save-orgs-btn"
                className="bg-orange text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy transition-colors duration-200 disabled:opacity-60">
                Save selection ({selected.size})
              </button>
            </div>
          </div>
          <div className="divide-y divide-navy/10">
            {status.organisations.map((o) => {
              const on = selected.has(o.tenant_id);
              return (
                <div key={o.tenant_id} data-testid={`org-row-${o.tenant_id}`}
                  className="flex items-center gap-2 pr-4 hover:bg-slate-50 transition-colors">
                  <button onClick={() => toggle(o.tenant_id)} data-testid={`org-toggle-${o.tenant_id}`}
                    className="flex-1 flex items-center gap-4 px-6 py-4 text-left min-w-0">
                    {on ? <CheckCircle size={22} weight="fill" className="text-orangeink shrink-0" />
                        : <Circle size={22} className="text-navy/25 shrink-0" />}
                    <Buildings size={20} className="text-navy/65 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-navy truncate">{o.tenant_name}</div>
                      <div className="font-mono text-xs text-navy/65 truncate">{o.tenant_id} · {o.country}</div>
                    </div>
                    {health[o.tenant_id] && (
                      <span data-testid={`org-health-${o.tenant_id}`}
                        className={`flex items-center gap-1.5 text-xs font-medium mr-1 ${health[o.tenant_id].ok ? "text-positive" : "text-negative"}`}
                        title={health[o.tenant_id].message}>
                        <span className={`w-2 h-2 rounded-full ${health[o.tenant_id].ok ? "bg-positive" : "bg-negative"}`} />
                        {health[o.tenant_id].ok ? "Connected" : "Failed"}
                      </span>
                    )}
                    {on && <span className="overline text-orangeink">Selected</span>}
                  </button>
                  {isAdmin && (
                    <button onClick={() => setDisconnectOrg(o)} data-testid={`org-disconnect-${o.tenant_id}`}
                      title={`Disconnect ${o.tenant_name}`}
                      className="shrink-0 p-2 rounded-lg text-navy/65 hover:text-negative hover:bg-negative/10 transition-colors">
                      <Trash size={18} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(status?.organisations || []).length > 0 && <ImportDiagnostic />}

      <DisconnectClientModal open={!!disconnectOrg} org={disconnectOrg}
        onClose={() => setDisconnectOrg(null)}
        onDone={() => { setDisconnectOrg(null); load(); window.dispatchEvent(new Event("badges:refresh")); }} />

      {user?.role === "admin" && (
        <div className="mt-6 bg-white border border-orange/30 rounded-lg p-6 flex items-center justify-between flex-wrap gap-4" data-testid="demo-zone">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange/10 text-orangeink flex items-center justify-center shrink-0">
              <Sparkle size={22} weight="fill" />
            </div>
            <div>
              <div className="font-heading font-bold text-navy tracking-tight">Load WorkWithLottie demo</div>
              <div className="text-sm text-navy/70 mt-0.5 max-w-xl">
                Populate a sales-ready demo — two demo entities with prepayments, accruals, deferred income,
                depreciation, part-posted schedules, open variances to fix, pending approvals and review notes —
                so every screen and report has content. Connect real Xero and use "Clear workspace data" when you go live.
              </div>
            </div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="load-demo-btn" disabled={busy}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors duration-200 disabled:opacity-60 shrink-0">
                <Sparkle size={16} weight="fill" /> {demoLoaded ? "Reset demo to fresh state" : "Load demo"}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-lg">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading tracking-tight">
                  {demoLoaded ? "Reset the WorkWithLottie demo?" : "Load the WorkWithLottie demo?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {demoLoaded
                    ? "This restores the demo to its pristine state — clearing any journals you posted, variances you fixed or notes you added during a walkthrough — and re-seeds the full demo data set. It only affects the demo entities and runs in mock mode."
                    : "This seeds two demo organisations and a full set of demo data into your workspace (replacing any existing demo data). It runs in mock mode and won't touch a live Xero connection. You can remove it anytime with \"Clear workspace data\"."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="load-demo-cancel" className="rounded-lg">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="load-demo-confirm" onClick={seedDemo}
                  className="rounded-lg bg-orange hover:bg-navy text-white">
                  {demoLoaded ? "Yes, reset demo" : "Yes, load demo"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <div className="mt-6 bg-white border border-negative/30 rounded-lg p-6 flex items-center justify-between flex-wrap gap-4" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-negative/10 text-negative flex items-center justify-center shrink-0">
            <Warning size={22} weight="fill" />
          </div>
          <div>
            <div className="font-heading font-bold text-navy tracking-tight">Clear data for this organisation</div>
            <div className="text-sm text-navy/70 mt-0.5 max-w-xl">
              Remove all imported items, release schedules, journals and audit history (e.g. test/mock data) for
              <b className="text-navy"> {activeOrgName || "the active organisation"}</b> only, so you can start fresh.
              Your other connected organisations, your Xero connection and account-code settings are all kept.
            </div>
          </div>
        </div>
        <AlertDialog open={showClear} onOpenChange={(o) => { setShowClear(o); if (!o) setResetPassword(""); }}>
          <AlertDialogTrigger asChild>
            <button data-testid="clear-data-btn" disabled={busy}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg border border-negative/40 text-negative hover:bg-negative hover:text-white transition-colors duration-200 disabled:opacity-60 shrink-0">
              <Trash size={16} /> Clear data
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading tracking-tight" data-testid="clear-data-title">
                Clear all data for {activeOrgName || "this organisation"}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes every imported item, release schedule, journal, close period and audit
                entry for <b>{activeOrgName || "the active organisation"}</b> only. Your other connected
                organisations are <b>not</b> affected, and your Xero connection and account-code settings stay
                intact. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-1">
              <label className="block text-sm font-medium text-navy mb-1.5">
                Enter your password to confirm clearing <b>{activeOrgName || "this organisation"}</b>
              </label>
              <input type="password" autoComplete="current-password" value={resetPassword} data-testid="clear-data-password"
                onChange={(e) => setResetPassword(e.target.value)} placeholder="Your account password"
                onKeyDown={(e) => { if (e.key === "Enter" && resetPassword) resetData(); }}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-negative focus:ring-2 focus:ring-negative/20" />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="clear-data-cancel" className="rounded-lg">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="clear-data-confirm" disabled={!resetPassword || busy}
                onClick={(e) => { e.preventDefault(); resetData(); }}
                className="rounded-lg bg-negative hover:bg-negative/90 text-white disabled:opacity-50">
                {busy ? "Clearing…" : `Yes, clear ${activeOrgName || "this organisation"}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
}

function ImportDiagnostic() {
  const [code, setCode] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [plAccounts, setPlAccounts] = useState([]);
  const [bulkCfg, setBulkCfg] = useState({ frequency: "monthly", num_periods: 12, release_basis: "monthly", pl_account_code: "" });
  const [bulkSelected, setBulkSelected] = useState(() => new Set());

  const toggleBulk = () => {
    setBulkOpen((o) => {
      const next = !o;
      if (next) setBulkSelected(new Set(eligible.map((r) => r.source_id)));
      return next;
    });
  };
  const toggleLine = (sid) => setBulkSelected((s) => {
    const n = new Set(s); n.has(sid) ? n.delete(sid) : n.add(sid); return n;
  });
  const endForDisplay = (startISO) => {
    if (!startISO || bulkCfg.frequency === "full") return startISO || "—";
    const n = Math.max(1, Number(bulkCfg.num_periods) || 1);
    const d = new Date(startISO + "T00:00:00");
    if (bulkCfg.frequency === "weekly") { d.setDate(d.getDate() + 7 * (n - 1)); }
    else { const m = { monthly: 1, quarterly: 3, annual: 12 }[bulkCfg.frequency] || 1; d.setMonth(d.getMonth() + m * (n - 1)); }
    return d.toISOString().slice(0, 10);
  };

  const runBulk = async () => {
    if (!bulkCfg.pl_account_code) { toast.error("Choose a P&L code to release to"); return; }
    const ids = Array.from(bulkSelected);
    if (ids.length === 0) { toast.error("Select at least one line to create"); return; }
    setBulkBusy(true);
    try {
      const body = { code: res.code, frequency: bulkCfg.frequency, num_periods: Number(bulkCfg.num_periods),
        release_basis: bulkCfg.release_basis, pl_account_code: bulkCfg.pl_account_code, source_ids: ids };
      if (fromDate) body.from_date = fromDate;
      const { data } = await api.post("/xero/import-diagnostic/bulk-schedule", body);
      toast.success(`Created ${data.created} release-only schedule(s)${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setBulkOpen(false);
      run();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk create failed");
    } finally { setBulkBusy(false); }
  };

  const run = async () => {
    if (!code.trim()) { toast.error("Enter a Xero account code"); return; }
    setBusy(true); setRes(null);
    try {
      const params = { code: code.trim() };
      if (fromDate) params.from_date = fromDate;
      const { data } = await api.get("/xero/import-diagnostic", { params });
      setRes(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnostic failed");
    } finally { setBusy(false); }
  };

  const badge = (d) => d === "importable" ? "bg-orange/15 text-orangeink"
    : d === "imported" ? "bg-positive/10 text-positive" : "bg-slate-100 text-navy/55";

  useEffect(() => {
    if (!res || !res.configured_as) { setPlAccounts([]); return; }
    const cls = res.configured_as === "DEFERRED_INCOME" ? "REVENUE" : "EXPENSE";
    api.get("/accounts").then(({ data }) => {
      const pl = (data.accounts || []).filter((a) => a.class === cls);
      setPlAccounts(pl);
      setBulkCfg((c) => ({ ...c, pl_account_code: c.pl_account_code || pl[0]?.code || "" }));
    }).catch(() => {});
  }, [res]);

  const eligible = res && res.configured_as
    ? res.results.filter((r) => r.decision !== "imported" && (r.amount || 0) > 0
        && r.side === (res.configured_as === "PREPAYMENT" ? "debit" : "credit"))
    : [];

  return (
    <div className="mt-6 bg-white border border-navy/15 rounded-lg p-6" data-testid="import-diagnostic">
      <div className="flex items-start gap-3 mb-1">
        <div className="w-10 h-10 rounded-lg bg-navy/[0.06] text-navy flex items-center justify-center shrink-0"><MagnifyingGlass size={20} /></div>
        <div>
          <h3 className="font-heading font-bold text-navy tracking-tight">Import diagnostic</h3>
          <p className="text-sm text-navy/65">Not seeing a Spend Money, Bill or journal in To-Review? Enter its Xero account code to see every matching line WWL found — and, for anything not imported, exactly why.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mt-4">
        <div>
          <label className="overline text-navy/60 block mb-1">Account code</label>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. 1201"
            data-testid="diag-code-input"
            className="w-40 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
        </div>
        <div>
          <label className="overline text-navy/60 block mb-1">From date (optional)</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            data-testid="diag-from-date"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
        </div>
        <button onClick={run} disabled={busy} data-testid="diag-run-btn"
          className="rounded-lg bg-navy text-white px-5 py-2 text-sm font-semibold hover:bg-orange transition-colors disabled:opacity-60">
          {busy ? "Checking…" : "Run diagnostic"}
        </button>
      </div>

      {res && (
        <div className="mt-5" data-testid="diag-results">
          <div className="flex flex-wrap gap-2 text-xs mb-3">
            <span className="px-2.5 py-1 rounded-full bg-navy/[0.06] text-navy/75">
              Code {res.code}: {res.configured_as ? `configured as ${res.configured_as.replace("_", " ").toLowerCase()}` : "NOT configured as a code"}
            </span>
            {res.from_date && <span className="px-2.5 py-1 rounded-full bg-navy/[0.06] text-navy/75">Import-from {res.from_date}</span>}
            <span className="px-2.5 py-1 rounded-full bg-orange/15 text-orangeink">{res.summary.importable} importable</span>
            <span className="px-2.5 py-1 rounded-full bg-positive/10 text-positive">{res.summary.imported} already in</span>
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-navy/55">{res.summary.skipped} skipped</span>
          </div>
          {(res.notes || []).map((n, i) => <p key={i} className="text-xs text-negative mb-1">⚠ {n}</p>)}
          {eligible.length > 0 && (
            <div className="mb-4">
              <button onClick={toggleBulk} data-testid="diag-bulk-open-btn"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg bg-orange text-white hover:bg-navy transition-colors">
                <Sparkle size={14} weight="fill" /> Create schedules for all missed lines ({eligible.length})
              </button>
              {bulkOpen && (
                <div className="mt-3 border border-orange/30 bg-orange/[0.04] rounded-lg p-4" data-testid="diag-bulk-panel">
                  <p className="text-xs text-navy/70 mb-3">
                    Creates a <b>release-only</b> schedule (no set-up journal) for each of the {eligible.length} missed
                    {" "}{res.configured_as.replace("_", " ").toLowerCase()} line(s), using the settings below. Each line keeps
                    its own amount, date &amp; reference.
                  </p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="overline text-navy/60 block mb-1">Frequency</label>
                      <select value={bulkCfg.frequency} data-testid="bulk-frequency"
                        onChange={(e) => setBulkCfg((c) => ({ ...c, frequency: e.target.value }))}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300">
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annual">Annual</option>
                        <option value="full">Release in full</option>
                      </select>
                    </div>
                    <div>
                      <label className="overline text-navy/60 block mb-1">Number of periods</label>
                      <input type="number" min="1" value={bulkCfg.num_periods} data-testid="bulk-num-periods"
                        onChange={(e) => setBulkCfg((c) => ({ ...c, num_periods: e.target.value }))}
                        disabled={bulkCfg.frequency === "full"}
                        className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:bg-slate-50" />
                    </div>
                    <div>
                      <label className="overline text-navy/60 block mb-1">Release to P&amp;L code</label>
                      <select value={bulkCfg.pl_account_code} data-testid="bulk-pl-code"
                        onChange={(e) => setBulkCfg((c) => ({ ...c, pl_account_code: e.target.value }))}
                        className="min-w-[220px] rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300">
                        {plAccounts.length === 0 && <option value="">Loading…</option>}
                        {plAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                      </select>
                    </div>
                    <button onClick={runBulk} disabled={bulkBusy} data-testid="bulk-create-btn"
                      className="rounded-lg bg-navy text-white px-5 py-2 text-sm font-semibold hover:bg-orange transition-colors disabled:opacity-60">
                      {bulkBusy ? "Creating…" : `Create ${bulkSelected.size} schedule(s)`}
                    </button>
                  </div>

                  <div className="mt-4 border-t border-orange/20 pt-3" data-testid="bulk-preview">
                    <div className="flex items-center justify-between mb-2">
                      <div className="overline text-navy/60">Preview — {bulkSelected.size} of {eligible.length} selected</div>
                      <div className="flex gap-3 text-xs">
                        <button onClick={() => setBulkSelected(new Set(eligible.map((r) => r.source_id)))}
                          data-testid="bulk-select-all" className="text-orangeink hover:underline font-medium">Select all</button>
                        <button onClick={() => setBulkSelected(new Set())}
                          data-testid="bulk-select-none" className="text-navy/60 hover:underline font-medium">Clear</button>
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg bg-white">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-slate-200 bg-slate-50 overline text-navy/60">
                          <th className="px-3 py-2 w-8"></th><th className="px-3 py-2 text-left">Start date</th>
                          <th className="px-3 py-2 text-left">Contact / ref</th>
                          <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">Schedule</th>
                        </tr></thead>
                        <tbody>
                          {eligible.map((r, i) => {
                            const on = bulkSelected.has(r.source_id);
                            return (
                              <tr key={r.source_id || i} data-testid={`bulk-line-${i}`}
                                className={`border-b border-slate-100 last:border-0 ${on ? "" : "opacity-45"}`}>
                                <td className="px-3 py-2">
                                  <input type="checkbox" checked={on} data-testid={`bulk-line-check-${i}`}
                                    onChange={() => toggleLine(r.source_id)}
                                    className="h-4 w-4 rounded border-slate-300 text-orange focus:ring-orange/40" />
                                </td>
                                <td className="px-3 py-2 font-mono text-xs">{r.date || "—"}</td>
                                <td className="px-3 py-2 max-w-[220px] truncate" title={`${r.contact} ${r.reference}`}>{r.contact}{r.reference ? ` · ${r.reference}` : ""}</td>
                                <td className="px-3 py-2 text-right font-mono">{Math.abs(r.amount || 0).toLocaleString("en-GB", { style: "currency", currency: "GBP" })}</td>
                                <td className="px-3 py-2 text-xs text-navy/60">
                                  {bulkCfg.frequency === "full" ? "Full on start" : `${bulkCfg.num_periods}× ${bulkCfg.frequency} → ${endForDisplay(r.date)}`}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {res.count === 0 ? (
            <p className="text-sm text-navy/65" data-testid="diag-empty">No transactions to account {res.code} were found in Xero for this window. Check the code is right and that the date range covers the transaction.</p>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead><tr className="border-b border-slate-200 bg-slate-50 overline text-navy/65">
                  <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Contact / ref</th><th className="px-3 py-2 text-left">Side</th>
                  <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">Result</th>
                </tr></thead>
                <tbody>
                  {res.results.map((r, i) => (
                    <tr key={i} data-testid={`diag-row-${i}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2 font-mono text-xs">{r.date || "—"}</td>
                      <td className="px-3 py-2">{r.source} <span className="text-navy/45 text-xs">({r.status})</span></td>
                      <td className="px-3 py-2 max-w-[200px] truncate" title={`${r.contact} ${r.reference}`}>{r.contact}{r.reference ? ` · ${r.reference}` : ""}</td>
                      <td className="px-3 py-2 capitalize">{r.side}</td>
                      <td className="px-3 py-2 text-right font-mono">{(r.amount || 0).toLocaleString("en-GB", { style: "currency", currency: "GBP" })}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[0.65rem] font-semibold uppercase px-2 py-0.5 rounded-full ${badge(r.decision)}`} data-testid={`diag-decision-${i}`}>{r.decision}</span>
                        <div className="text-xs text-navy/60 mt-0.5">{r.reason}</div>
                        {res.configured_as && r.decision !== "imported" && (r.amount || 0) > 0 && (
                          <button data-testid={`diag-create-schedule-${i}`}
                            onClick={() => setPrefill({
                              category: res.configured_as, source_account_code: res.code,
                              amount: Math.abs(r.amount || 0), date: r.date,
                              reference: r.reference || r.contact || res.code,
                              description: `${r.contact || ""}${r.reference ? ` · ${r.reference}` : ""}`.trim(),
                            })}
                            className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 text-[0.68rem] font-semibold rounded-lg border border-orange/40 text-orangeink hover:bg-orange hover:text-white transition-colors">
                            <Sparkle size={12} weight="fill" /> Create release-only schedule
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <CreateAdjustmentModal open={!!prefill} prefill={prefill}
        onClose={() => setPrefill(null)}
        onCreated={() => { setPrefill(null); toast.success("Release-only schedule created — see the Register."); }} />
    </div>
  );
}


export default function Connections() {
  return <Page><ConnectionsPanel /></Page>;
}
